var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { Component, Model, Watch, Prop } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import ReleaseRosterDialog from '@/views/RosterManagement/ReleaseRosterDialog';
import { defaultCalendarSettings, dayMap } from '@/support/CalendarDefaults';
import ErrorHandler from '@/support/ErrorHandler';
import { Teacher } from '@/models/Teacher';
import { Subject } from '@/models/Subject';
import { Room } from '@/models/Room';
import { Level } from '@/models/Level';
import { Group } from '@/models/Group';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import GenerateRosterDialog from '@/views/RosterManagement/GenerateRosterDialog';
import { DateTime } from 'luxon';
import LoaderCard from '@/components/loader-card/LoaderCard';
import AddEventDialog from '@/components/mi-calendar/AddEventDialog';
import { renderCustomTimeslot } from '@/support/CalendarEventRenderFunctions';
import ShowEventsDialog from '@/views/RosterManagement/ShowEventsDialog';
import { User } from '@/models/User';
import ChooseEventDialog from './Dialogs/ChooseEventDialog';
import { Student } from '@/models/Student';
import { DateTimeslotSummary } from '@/models/DateTimeslotSummary';
import InformationDialog from './InformationDialog';
import AbstractUrl from '@/support/AbstractUrl';
import { isEmpty, isArray } from 'lodash';
let Roster = class Roster extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.fullBusinessHours = {
            daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
            startTime: '00:00',
            endTime: '00:00',
        };
        this.isReleasingRoster = false;
        this.isGeneratingRosterDialog = false;
        this.isLoading = false;
        this.eventData = null;
        this.events = [];
        this.clearSelects = false;
        this.isUnsafeMode = false;
        this.isShowingEventsDialog = false;
        this.isChosingEvent = false;
        this.isInHardLock = false;
        this.calendarOptionsUpdated = false;
        this.isEventEditable = false;
        this.showSafeModeButton = true;
        this.isShowingInformationDialog = false;
        this.hasWeekends = false;
        this.subjectModel = new Subject;
        this.subjects = [];
        this.roomModel = new Room;
        this.room = '';
        this.teacherModel = new Teacher;
        this.levelModel = new Level;
        this.levels = [];
        this.groupModel = new Group().sort('name');
        this.groups = [];
        this.selectedEventId = null;
        this.selectedEvents = null;
        this.selectedStart = '';
        this.selectedEnd = 'null';
        this.currentWeekStart = '';
        this.selectedDatetimeslot = null;
    }
    get eventFilter() {
        return {
            users: this.student ? [this.student.id] : [this.$store.state.Auth.id] || [],
            room: this.room,
            levels: this.levels,
            groups: this.groups,
            subjects: this.subjects,
            types: ['lesson', 'recess'],
            is_pickable: true,
        };
    }
    get weekStart() {
        const setting = this.$store.state.settings.find((setting) => {
            return setting.key === 'first_day_of_week';
        });
        if (setting) {
            this.hasWeekends = this.hasWeekendsInSchoolTimes() ? this.hasWeekendsInSchoolTimes() : ['sunday', 'saturday'].includes(setting.value);
            return dayMap[setting.value];
        }
        return 1;
    }
    get calendarOptions() {
        return {
            ...defaultCalendarSettings,
            ...{
                editable: false,
                buttonText: {
                    today: this.$t('rosterManagement.today'),
                },
                firstDay: this.weekStart,
                weekends: this.hasWeekends,
                eventOverlap: false,
                businessHours: this.fullBusinessHours,
                eventConstraint: 'businessHours',
                selectConstraint: 'businessHours',
                eventClick: (selectionInfo) => {
                    // Hard lock prevents click
                    this.selectedEvents = selectionInfo.event.extendedProps.events;
                    this.selectedDatetimeslot = selectionInfo.event.id;
                    const start = DateTime.fromJSDate(selectionInfo.event.start).toMillis();
                    const lock = DateTime.fromSQL(this.hardLockDate || '').toMillis();
                    if (start <= lock) {
                        this.isInHardLock = true;
                    }
                    // Only allow popup for event with more than 1 subject.
                    // if(!this.selectedEvents || this.selectedEvents.length <= 1) {
                    //   return false;
                    // }
                    this.selectedStart = DateTime.fromJSDate(selectionInfo.event.start).toFormat('yyyy-LL-dd HH:mm');
                    this.selectedEnd = DateTime.fromJSDate(selectionInfo.event.end).toFormat('yyyy-LL-dd HH:mm');
                    history.pushState({}, '', `/roster/${this.selectedStudentId}/slot/${selectionInfo.event.id}`);
                    if (this.selectedEvents) {
                        this.isChosingEvent = true;
                    }
                },
                eventRender: (eventInfo) => {
                    if (!eventInfo.event.id) {
                        return true;
                    }
                    // const type = this.filterType;
                    const activities = this.$tc('roster.activities', 2);
                    return renderCustomTimeslot(eventInfo, 'students', this.getEventClass(eventInfo), activities);
                },
                datesRender: (info) => {
                    const calendarStart = DateTime.fromJSDate(info.view.activeStart).toFormat('yyyy-LL-dd');
                    if (this.currentWeekStart !== calendarStart) {
                        this.currentWeekStart = calendarStart;
                        const calendarEnd = DateTime.fromJSDate(info.view.activeEnd).toFormat('yyyy-LL-dd');
                        this.fetchDateTimeslots(calendarStart, calendarEnd);
                    }
                },
            },
        };
    }
    get filterType() {
        if (this.room.length) {
            return 'rooms';
        }
        else if (this.levels.length) {
            return 'levels';
        }
        else if (this.groups.length) {
            return 'groups';
        }
        else if (this.subjects.length) {
            return 'subjects';
        }
        else {
            return null;
        }
    }
    get breadcrumbs() {
        return [{ name: 'sCoolPlanner', path: `/roster/${this.selectedStudentId}` }];
    }
    get hardLockDate() {
        const hard_lock = this.$store.state.settings.find((setting) => {
            return setting.key === 'hard_lock_date';
        });
        return hard_lock.value + ' 23:59:59';
    }
    get selectedStudentId() {
        if (this.student && this.student.id) {
            return this.student.id;
        }
        return this.$store.state.Auth.id;
    }
    mounted() {
        const currentDayNumber = parseInt(DateTime.local().toFormat('c'));
        this.currentWeekStart = DateTime.local().minus({ days: currentDayNumber - 1 }).toFormat('yyyy-LL-dd');
        const end = DateTime.local().plus({ days: 7 - currentDayNumber }).toFormat('yyyy-LL-dd');
        if (this.$route.query && !isEmpty(this.$route.query)) {
            this.$nextTick(() => {
                this.preSetFilters(this.$route.query);
                this.preFillSelects(this.$route.query);
                this.fetchDateTimeslots(this.currentWeekStart, end);
                this.isLoading = true;
            });
            return;
        }
        this.fetchDateTimeslots(this.currentWeekStart, end);
        this.isLoading = true;
    }
    fetchDateTimeslots(starts, ends) {
        this.events = [];
        new DateTimeslotSummary()
            .filter({ ...this.eventFilter, ...{ starts_at: starts, ends_at: ends } })
            .limit(100)
            .load()
            .then((response) => {
            const summary = new DateTimeslotSummary({ data: response.data.data, meta: response.data.meta });
            this.events = summary.calendarEvents;
            this.isLoading = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    preSetFilters(filters) {
        if (filters.subjects) {
            this.subjects = isArray(filters.subjects) ? filters.subjects : [filters.subjects];
        }
        if (filters.room) {
            this.room = filters.room;
        }
        if (filters.levels) {
            this.levels = isArray(filters.levels) ? filters.levels : [filters.levels];
        }
        if (filters.groups) {
            this.groups = isArray(filters.groups) ? filters.groups : [filters.groups];
        }
    }
    async preFillSelects(filters) {
        if (filters.subjects && this.$refs.subjectSelect) {
            this.preFillSubjectSelect(isArray(filters.subjects) ? filters.subjects : [filters.subjects]);
        }
        if (filters.room && this.$refs.roomSelect) {
            this.preFillRoomSelect(isArray(filters.room) ? filters.room : [filters.room]);
        }
        if (filters.levels && this.$refs.levelSelect) {
            this.preFillLevelSelect(isArray(filters.levels) ? filters.levels : [filters.levels]);
        }
        if (filters.groups && this.$refs.groupSelect) {
            this.preFillGroupSelect(isArray(filters.groups) ? filters.groups : [filters.groups]);
        }
    }
    async preFillRoomSelect(roomIds) {
        const rooms = await new Room()
            .filter('ids', roomIds)
            .all();
        this.$refs.roomSelect.setDefaultValue(rooms.map((room) => ({
            value: room.id,
            text: room.name,
        })), false);
    }
    async preFillSubjectSelect(subjectIds) {
        const subjects = await new Subject()
            .filter('ids', subjectIds)
            .all();
        this.$refs.subjectSelect.setDefaultValue(subjects.map((subject) => ({
            value: subject.id,
            text: subject.name,
        })), false);
    }
    async preFillLevelSelect(levelIds) {
        const levels = await new Level()
            .filter('ids', levelIds)
            .all();
        this.$refs.levelSelect.setDefaultValue(levels.map((level) => ({
            value: level.id,
            text: level.name,
        })), false);
    }
    async preFillGroupSelect(groupIds) {
        const groups = await new Group()
            .filter('ids', groupIds)
            .all();
        this.$refs.groupSelect.setDefaultValue(groups.map((group) => ({
            value: group.id,
            text: group.name,
        })), false);
    }
    hasWeekendsInSchoolTimes() {
        const saturday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'saturday';
        });
        const sunday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'sunday';
        });
        return sunday.starts_at !== '00:00:00' && sunday.ends_at !== '00:00:00' || saturday.starts_at !== '00:00:00' && saturday.ends_at !== '00:00:00';
    }
    viewCalendarEvent(selectionInfo) {
        //
    }
    isFiltered() {
        return this.subjects.length || this.room.length || this.levels.length || this.groups.length;
    }
    resetFilters() {
        this.room = '';
        this.levels = [];
        this.groups = [];
        this.subjects = [];
        this.clearSelects = true;
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.isLoading = true;
        this.fetchDateTimeslots(starts, ends);
    }
    formatJSDate(date) {
        return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
    }
    isBeforeDate(checkDate, referenceDate) {
        if (checkDate.month < referenceDate.month) {
            return true;
        }
        if (checkDate.month === referenceDate.month && checkDate.day <= referenceDate.day) {
            return true;
        }
        return false;
    }
    dialogChanged(isShowing) {
        if (!isShowing) {
            this.isEventEditable = false;
        }
    }
    getEventClass(eventInfo) {
        const eventStartDate = DateTime.fromMillis(Date.parse(eventInfo.event.start));
        const hardLockDate = DateTime.fromFormat(this.hardLockDate || '', 'yyyy-LL-dd HH:mm:ss');
        if (eventStartDate.toMillis() <= hardLockDate.toMillis()) {
            return 'fc-hard_lock';
        }
        const events = eventInfo.event.extendedProps.events;
        let className = 'attention';
        let isChosen = false;
        if (!events) {
            return className = '';
        }
        events.forEach((event) => {
            if (event.is_chosen) {
                isChosen = true;
            }
        });
        if (isChosen) {
            className = 'chosen';
        }
        else {
            className = 'attention';
        }
        return className;
    }
    reloadCalendarEvents() {
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.fetchDateTimeslots(starts, ends);
    }
    handleEventChosen() {
        this.reloadCalendarEvents();
    }
    dialogClosed() {
        this.isInHardLock = false;
    }
    filterChanged(value) {
        if (!this.isLoading) {
            this.isLoading = true;
            const filters = { ...this.eventFilter };
            this.setQueryParams(filters);
            this.reloadCalendarEvents();
        }
    }
    changedCurrentWeekStart() {
        this.isLoading = true;
    }
};
__decorate([
    Model('student', { default: () => new User() }),
    __metadata("design:type", typeof (_a = typeof User !== "undefined" && User) === "function" ? _a : Object)
], Roster.prototype, "user", void 0);
__decorate([
    Prop({ default: null }),
    __metadata("design:type", typeof (_b = typeof Student !== "undefined" && Student) === "function" ? _b : Object)
], Roster.prototype, "student", void 0);
__decorate([
    Watch('isShowingEventsDialog'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], Roster.prototype, "dialogChanged", null);
__decorate([
    Watch('room'),
    Watch('groups'),
    Watch('levels'),
    Watch('subjects'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Roster.prototype, "filterChanged", null);
__decorate([
    Watch('currentWeekStart'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Roster.prototype, "changedCurrentWeekStart", null);
Roster = __decorate([
    Component({
        components: {
            PageHeader,
            ReleaseRosterDialog,
            GenerateRosterDialog,
            AsyncSelect,
            LoaderCard,
            AddEventDialog,
            ShowEventsDialog,
            ChooseEventDialog,
            InformationDialog,
        },
    })
], Roster);
export default Roster;
